/*
  7Raw product page runtime (sections/7r-main-product.liquid; docs/screens/05-product.md §6, A6, A05-5).

  <r7-product-swap data-section-id data-also-in-id data-page-title> wraps the product section:
  - Gallery: a thumbnail click shows that slide (instant swap, no fade) and moves aria-current. Slides 2+
    are rendered hidden with loading="lazy", which never loads inside display:none, so once the page has
    loaded (on idle) or on the first thumbnail click they are switched to eager and fetched ahead of use.
  - Text accordions ([data-r7-trim]): the server renders the full text; here it is stored once per
    element (WeakMap, before the first trim, so a reconnect never re-trims trimmed text), trimmed to
    data-sentences sentences and its "Read more" button is revealed. Only one text is expanded at a time.
    Sentences end at whitespace after . ! or ?, optionally followed by " or ” (the prototype's split,
    written without a lookbehind so older Safari can parse it).
  - Accordions: <details name> is exclusive natively; the toggle handler does the same where `name` is
    not supported.
  - Quantity: a typed value is clamped to the input's min/max.
  - Product swap: Format / Scent / variant links (a[data-r7-swap]) fetch this section and the `also-in`
    section through the Section Rendering API (?sections=, no Accept header), replace both, keep the scroll
    position and the open accordion, fade in when the page fade is on, and push the URL. Back/forward
    swap again. Any failure falls back to a normal page load. Without JS the links simply navigate.
*/
(() => {
  if (customElements.get('r7-product-swap')) return;

  const SENTENCE_END = /([.!?]["”]?)\s+/;

  /* Full text of each [data-r7-trim] paragraph, read once before its first trim. A reconnect of the
     element (or a second initTexts) must never read the already trimmed text back as the full one.
     A section re-render brings new paragraphs, which get their own entry. */
  const fullTexts = new WeakMap();

  const splitSentences = (text) => {
    const parts = text.split(SENTENCE_END);
    const sentences = [];
    for (let i = 0; i < parts.length; i += 2) {
      const sentence = parts[i] + (parts[i + 1] || '');
      if (sentence) sentences.push(sentence);
    }
    return sentences;
  };

  const reducedMotion = () =>
    window.R7
      ? window.R7.prefersReducedMotion()
      : document.documentElement.classList.contains('r7-rm') &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let activeRequest = null;

  class R7ProductSwap extends HTMLElement {
    connectedCallback() {
      this.texts = new Map();
      this.onClick = this.onClick.bind(this);
      this.onToggle = this.onToggle.bind(this);
      this.onChange = this.onChange.bind(this);
      this.preloadSlides = this.preloadSlides.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('toggle', this.onToggle, true);
      this.addEventListener('change', this.onChange);
      this.initTexts();
      this.scheduleSlidePreload();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('toggle', this.onToggle, true);
      this.removeEventListener('change', this.onChange);
    }

    onClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const thumb = target.closest('.r7-pdp__thumb');
      if (thumb && this.contains(thumb)) {
        this.showSlide(thumb.dataset.index);
        return;
      }

      const more = target.closest('.r7-pdp__more');
      if (more && this.contains(more)) {
        this.toggleText(more);
        return;
      }

      const link = target.closest('a[data-r7-swap]');
      if (!link || !this.contains(link) || !this.canSwap(event, link)) return;
      event.preventDefault();
      if (link.hasAttribute('aria-current')) return;
      const group = [...this.querySelectorAll(`a[data-r7-swap="${link.dataset.r7Swap}"]`)];
      this.swap(link.href, { push: true, focusGroup: link.dataset.r7Swap, focusIndex: group.indexOf(link) });
    }

    canSwap(event, link) {
      if (event.defaultPrevented || event.button !== 0) return false;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
      if (link.target && link.target !== '_self') return false;
      if (window.Shopify && window.Shopify.designMode) return false;
      if (!window.fetch || !window.DOMParser || !window.history.pushState) return false;
      return new URL(link.href, window.location.href).origin === window.location.origin;
    }

    /* Gallery */

    scheduleSlidePreload() {
      const idle = () => {
        if (window.requestIdleCallback) window.requestIdleCallback(this.preloadSlides, { timeout: 2000 });
        else window.setTimeout(this.preloadSlides, 200);
      };
      if (document.readyState === 'complete') idle();
      else window.addEventListener('load', idle, { once: true });
    }

    preloadSlides() {
      if (!this.isConnected) return;
      this.querySelectorAll('.r7-pdp__slide img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    }

    showSlide(index) {
      this.preloadSlides();
      this.querySelectorAll('.r7-pdp__slide').forEach((slide) => {
        slide.hidden = slide.dataset.index !== index;
      });
      this.querySelectorAll('.r7-pdp__thumb').forEach((thumb) => {
        thumb.setAttribute('aria-current', thumb.dataset.index === index ? 'true' : 'false');
      });
    }

    /* Sentence trimming + Read more */

    initTexts() {
      this.querySelectorAll('[data-r7-trim]').forEach((paragraph) => {
        const button = this.querySelector(`.r7-pdp__more[aria-controls="${paragraph.id}"]`);
        if (!fullTexts.has(paragraph)) fullTexts.set(paragraph, paragraph.textContent.trim());
        const full = fullTexts.get(paragraph);
        const limit = parseInt(paragraph.dataset.sentences, 10) || 3;
        const sentences = splitSentences(full);
        if (!button || sentences.length <= limit) return;

        this.texts.set(paragraph.id, { full, short: sentences.slice(0, limit).join(' ') });
        /* Keep the state on a reconnect (the server renders aria-expanded="false"). */
        this.setExpanded(button, button.getAttribute('aria-expanded') === 'true');
        button.hidden = false;
      });
    }

    toggleText(button) {
      const expand = button.getAttribute('aria-expanded') !== 'true';
      if (expand) {
        this.querySelectorAll('.r7-pdp__more[aria-expanded="true"]').forEach((other) => {
          if (other !== button) this.setExpanded(other, false);
        });
      }
      this.setExpanded(button, expand);
    }

    setExpanded(button, expanded) {
      const id = button.getAttribute('aria-controls');
      const text = this.texts.get(id);
      const paragraph = document.getElementById(id);
      if (!text || !paragraph) return;
      paragraph.textContent = expanded ? text.full : text.short;
      button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      button.textContent = expanded ? button.dataset.less : button.dataset.more;
    }

    /* Accordions and quantity */

    onToggle(event) {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.open || !details.classList.contains('r7-pdp__acc')) return;
      this.querySelectorAll('details.r7-pdp__acc[open]').forEach((other) => {
        if (other !== details) other.open = false;
      });
    }

    onChange(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.classList.contains('quantity__input')) return;
      const min = parseInt(input.min, 10) || 1;
      const max = parseInt(input.max, 10) || Infinity;
      const value = parseInt(input.value, 10);
      const clamped = Number.isNaN(value) ? min : Math.min(max, Math.max(min, value));
      if (String(clamped) !== input.value) input.value = clamped;
      const stepper = input.closest('quantity-input');
      if (stepper && typeof stepper.validateQtyRules === 'function') stepper.validateQtyRules();
    }

    /* Product swap (A6, A05-5) */

    async swap(href, { push = true, focusGroup = null, focusIndex = 0 } = {}) {
      const url = new URL(href, window.location.href);
      const mainId = this.dataset.sectionId;
      const alsoInId = this.dataset.alsoInId;
      const ids = [mainId];
      if (alsoInId && document.getElementById(`shopify-section-${alsoInId}`)) ids.push(alsoInId);

      const requestUrl = new URL(url.href);
      requestUrl.hash = '';
      requestUrl.searchParams.set('sections', ids.join(','));

      if (activeRequest) activeRequest.abort();
      const request = new AbortController();
      activeRequest = request;
      this.setAttribute('aria-busy', 'true');

      let sections;
      try {
        const response = await fetch(requestUrl.href, { signal: request.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        sections = await response.json();
        if (!sections || typeof sections[mainId] !== 'string') throw new Error('Missing section');
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.assign(url.href);
        return;
      } finally {
        if (activeRequest === request) activeRequest = null;
        this.removeAttribute('aria-busy');
      }

      const openDetails = this.querySelector('details.r7-pdp__acc[open]');
      const openKey = openDetails ? openDetails.dataset.key : null;
      const restoreFocus = Boolean(focusGroup) && this.contains(document.activeElement);
      const fade = !reducedMotion() && Boolean(document.querySelector('main.r7-page-in'));

      const loadedScripts = new Set([...document.scripts].map((script) => script.src));
      ids.forEach((id) => {
        const wrapper = document.getElementById(`shopify-section-${id}`);
        if (!wrapper || typeof sections[id] !== 'string') return;
        const parsed = new DOMParser().parseFromString(sections[id], 'text/html');
        const fresh = parsed.getElementById(`shopify-section-${id}`);
        wrapper.innerHTML = fresh ? fresh.innerHTML : '';
        R7ProductSwap.loadNewScripts(wrapper, loadedScripts);
        if (fade) R7ProductSwap.fadeIn(wrapper);
      });

      const next = document.querySelector(`#shopify-section-${mainId} r7-product-swap`);
      if (!next) {
        window.location.assign(url.href);
        return;
      }

      next.querySelectorAll('details.r7-pdp__acc').forEach((details) => {
        details.open = details.dataset.key === openKey;
      });

      if (next.dataset.pageTitle) document.title = next.dataset.pageTitle;
      if (push) {
        if (!(window.history.state && window.history.state.r7ProductSwap)) {
          window.history.replaceState({ ...(window.history.state || {}), r7ProductSwap: true }, '');
        }
        window.history.pushState({ r7ProductSwap: true }, '', url.href);
      }

      /* The clicked link was replaced: focus the new current link of its group, else the one at its place. */
      if (restoreFocus) {
        const group = [...next.querySelectorAll(`a[data-r7-swap="${focusGroup}"]`)];
        const target = group.find((link) => link.hasAttribute('aria-current')) || group[focusIndex] || group[0];
        if (target) target.focus({ preventScroll: true });
      }
    }

    /* Scripts inserted through innerHTML never run: load the ones this page has not loaded yet
       (e.g. the card runtime when the previous product had no "Also in" cards). */
    static loadNewScripts(wrapper, loadedScripts) {
      wrapper.querySelectorAll('script[src]').forEach((inert) => {
        if (loadedScripts.has(inert.src)) return;
        loadedScripts.add(inert.src);
        const script = document.createElement('script');
        script.src = inert.src;
        script.defer = true;
        document.head.appendChild(script);
      });
    }

    static fadeIn(wrapper) {
      wrapper.classList.remove('r7-swap-in');
      void wrapper.offsetWidth;
      wrapper.classList.add('r7-swap-in');
      const done = (event) => {
        if (event.target !== wrapper) return;
        wrapper.classList.remove('r7-swap-in');
        wrapper.removeEventListener('animationend', done);
      };
      wrapper.addEventListener('animationend', done);
    }
  }

  customElements.define('r7-product-swap', R7ProductSwap);

  window.addEventListener('popstate', (event) => {
    if (!(event.state && event.state.r7ProductSwap)) return;
    const element = document.querySelector('r7-product-swap');
    if (element) element.swap(window.location.href, { push: false });
  });
})();
